import { Cdata, HoneypotClassName, HoneypotConst, HoneypotStyles, HoneypotCssVariables } from "../structs";
import { Utils } from "../utils";
import { Challenge } from "./challenge";
import { ChallengerCore } from "../core";

interface HoneypotInputType {
    input: string;
    render: number;
}

interface HoneypotMedia {
    [id: string]: HoneypotInputType
}

function DefaultHoneypotStyles(): HoneypotStyles {
	return {
        honeypotText: 'inherit',
        honeypotCheckBackground: 'transparent',
        honeypotCheckBorder: 'inherit',
        honeypotCheckBackgroundHover: '#FB9A9A',
        honeypotCheckBackgroundFocus: '#FB9A9A',
        honeypotCheckBorderChecked: '#FB9A9A',
        honeypotCheckBackgroundChecked: '#BF0000',
        honeypotCheckMarker: 'white',
        honeypotCheckBorderRequired: '#BF0000',
        honeypotCheckRequired: '#DF0101',
        honeypotExplanation: 'inherit',
        honeypotOutline: 'inherit',
		honeypotFontFamily: 'Arial, sans-serif',
    }
}

export class HoneypotChallenge extends Challenge {
    #_shadowRoot: ShadowRoot;
    private inputFieldsDiv: HTMLDivElement;
    private honeypotDiv: HTMLDivElement;
    private honeypotStyles: HoneypotStyles | undefined;

    constructor(core: ChallengerCore, cdata: Cdata, cdata_status: number, mdata: string) {
        super(core, cdata, cdata_status, mdata);
        this.honeypotStyles = core.conf.honeypotStyles;
    }

    protected async execute(): Promise<boolean> {
        if (!this.mdata && !(await this.getMedia())) { return false; }
        return this.processMdata();
    }

    async render(parentDiv: HTMLElement): Promise<boolean> {
        if (!parentDiv) { return false; }

        const honeypotDiv = document.createElement("div");
        honeypotDiv.className = HoneypotClassName.HONEYPOT_DIV;
        this.honeypotDiv = honeypotDiv;

        if (this.core.conf.honeypotShadowDom) {        
            // Remove the shadowRoot container if its existing
            let shadowContainer = parentDiv.querySelector("#shadow-container");
            if (shadowContainer != null) {
                parentDiv.removeChild(shadowContainer);    
            }
            // Add the shadowRoot div container this is needed to remove the honeypot shadowRoot element from previous iterations
            shadowContainer = document.createElement("div");
            shadowContainer.id = "shadow-container";
            this.#_shadowRoot = shadowContainer.attachShadow({mode: "closed"});
            parentDiv.append(shadowContainer);
            
            if (this.honeypotStyles == undefined) {
                this.honeypotStyles = DefaultHoneypotStyles();
            }
            const styleTag = this.getStyleSheet(this.honeypotStyles);
            this.#_shadowRoot.append(styleTag);
            this.#_shadowRoot.appendChild(honeypotDiv);
        } else {
            parentDiv.appendChild(honeypotDiv);
        }
        parentDiv.appendChild(this.getCresElement());

        // Security required text element
        const securityRequiredDiv = document.createElement("div");
        securityRequiredDiv.innerHTML = this.core.conf.getLocalization().securityCheckRequired;
        securityRequiredDiv.className = HoneypotClassName.HONEYPOT_SECURITY;
        honeypotDiv.appendChild(securityRequiredDiv);

        // Parent div for input elements
        const inputFieldsDiv = document.createElement("div");
        inputFieldsDiv.className = HoneypotClassName.HONEYPOT_DIV_INPUTS;
        this.inputFieldsDiv = inputFieldsDiv;
        honeypotDiv.appendChild(inputFieldsDiv);

        const subDiv = document.createElement("div");
        honeypotDiv.appendChild(subDiv);

        // Check required span (hidden by default)
        const checkRequired = document.createElement("div");
        checkRequired.className = HoneypotClassName.HONEYPOT_CHECK_REQUIRED;
        checkRequired.innerHTML = this.core.conf.getLocalization().imNotaBotRequired;
        checkRequired.style.display = "none";
        subDiv.appendChild(checkRequired);

        // Explanation text
        const explanationDiv = document.createElement("div");
        explanationDiv.innerHTML = this.core.conf.getLocalization().imNotaBotExplanation;
        explanationDiv.className = HoneypotClassName.HONEYPOT_EXPLANATION;
        subDiv.appendChild(explanationDiv);

        if (!(await this.execute())) { return false; }

        return true;
    }

    protected processMdata(): boolean {
        let fields: HoneypotMedia;
        try {
            // Fields is a mapping of element ID to honeypot data with 'input' and 'render'
            this.mdata = this.mdata.replace(/\\/g, "");
            fields = JSON.parse(this.mdata);
        } catch (e) {
            return false;
        }

        // If first element of fields is the real checkbox, reshuffle the order
        // to make it harder for a bot to find it
        const fieldKeys = Object.keys(fields);

        if (fieldKeys.length > 0 && fields[fieldKeys[0]].render == HoneypotConst.INPUT_TYPE_NOT_A_BOT) {
            const tempIndex = Math.floor(fieldKeys.length / 2);
            const temp = fieldKeys[tempIndex];
            fieldKeys[tempIndex] = fieldKeys[0];
            fieldKeys[0] = temp;
        }

        // Iterate through fields and create the inputs
        for (const id of fieldKeys) {
            // Create input element dynamically based on render
            // Store ID and input type to be checked later
            const honeypotField = document.createElement("input");
            honeypotField.id = id;
            honeypotField.type = fields[id].input;
            honeypotField.autocomplete = "off";

            // Create and associated label
            const honeypotLabel = document.createElement("label");
            honeypotLabel.htmlFor = honeypotField.id;

            // All labels should have same inner text
            // HTML comment with random string is added to further confuse scripts searching for it
            const randomComment = Utils.randomStringGenerator(0, 5);
            honeypotLabel.innerHTML = "<!-- " + randomComment + " -->" + this.core.conf.getLocalization().imNotaBot + "<!---->";

            switch (fields[id].render) {
                case HoneypotConst.INPUT_TYPE_NOT_A_BOT: // Normal
                    // Should be the "I'm not a robot field"
                    honeypotField.className = HoneypotClassName.HONEYPOT_INPUT_NOTABOT;
                    honeypotLabel.className = HoneypotClassName.HONEYPOT_LABEL_NOTABOT;

                    // Add a listener to clear border color style whenever it's clicked
                    honeypotField.addEventListener("change", function () {
                        this.style.borderColor = "";
                    });
                    break;
                case HoneypotConst.INPUT_TYPE_DISPLAY_NONE: // display: none
                    honeypotField.className = HoneypotClassName.HONEYPOT_INPUT_TYPE1;
                    honeypotLabel.className = HoneypotClassName.HONEYPOT_LABEL_TYPE1;
                    break;
                case HoneypotConst.INPUT_TYPE_NO_SIZE: // Size 0
                    honeypotField.className = HoneypotClassName.HONEYPOT_INPUT_TYPE2;
                    honeypotLabel.className = HoneypotClassName.HONEYPOT_LABEL_TYPE2;
                    break;
                case HoneypotConst.INPUT_TYPE_OUTOFBOUNDS: // Out of bounds
                    honeypotField.className = HoneypotClassName.HONEYPOT_INPUT_TYPE3;
                    honeypotLabel.className = HoneypotClassName.HONEYPOT_LABEL_TYPE3;
                    break;
                default: // Display none
                    honeypotField.className = HoneypotClassName.HONEYPOT_INPUT_TYPE1;
                    honeypotLabel.className = HoneypotClassName.HONEYPOT_LABEL_TYPE1;
                    break;
            }

            this.inputFieldsDiv.appendChild(honeypotLabel);
            this.inputFieldsDiv.insertBefore(honeypotField, honeypotLabel);
        }

        return true;
    }

    // Final result is combination of all honeypot fileds, including the correct one
    // If "Not a bot" input is unchecked, recommend blocking submit / login
    getChallengeResult(): [string, boolean] {
        let allowFormSubmit = true;
        let result = "";
        // Get all honeypot fields under the inputs div
        // The 'cres' input will also be here
        const honeypotFields = this.inputFieldsDiv.getElementsByTagName("input");

        for (let i = 0; i < honeypotFields.length; i++) {
            // For each field, depending on the type, get its value
            // and concatenate to result
            const inputField = honeypotFields.item(i);

            if (inputField) {
                // If this is the required input but not checked, recommend blocking form submit
                // and show 'Cannot be blank' text
                if (inputField && inputField.className == HoneypotClassName.HONEYPOT_INPUT_NOTABOT) {
                    const checkRequiredDiv = <HTMLElement>this.honeypotDiv.getElementsByClassName(
                        HoneypotClassName.HONEYPOT_CHECK_REQUIRED
                    ).item(0);
                        if (checkRequiredDiv) {
                            const displayValue = inputField.checked ? "none": "block";
                            checkRequiredDiv.style.display = displayValue;
                        }
                        allowFormSubmit = inputField.checked;
                        inputField.style.borderColor = inputField.checked ? "" : HoneypotConst.NOT_A_BOT_UNSELECTED_BORDER_COLOR;
                }

                switch (inputField.type) {
                    case "checkbox":
                        if (inputField.checked) {
                            result = result + inputField.id;
                        }
                        break;
                    case "text":
                    case "password":
                    case "email":
                        if (inputField.value.length > 0) {
                            result = result + inputField.id;
                        }
                        break;
                    default:
                        if (inputField.value.length > 0) {
                            result = result + inputField.id;
                        }
                        break;
                }
            }
        }

        return [result, !allowFormSubmit];
    }

    private getStyleSheet(hs: HoneypotStyles): HTMLStyleElement {
		const styleTag = document.createElement("style");
		styleTag.textContent = `
            /* Honeypot input classes have hash like names to make it more difficult to find a pattern */
            .honeypot-field-w8i4k3l {
                appearance: none;
                background-color: var(${HoneypotCssVariables.checkboxBgColor}, ${
                    hs.honeypotCheckBackground
                });
                background-repeat: no-repeat;
                background-position: center center;
                background-size: 80%;
                border-color: var(${HoneypotCssVariables.checkboxBorderColor}, ${
                    hs.honeypotCheckBorder
                });
                border-radius: 0.188rem;
                border-style: solid;
                border-width: 0.125rem;
                width: 1.5rem;
                height: 1.5rem;
                margin-right: 10pt;
                display: inline-grid;
                position: relative;
                place-content: center;
                outline: ${hs.honeypotOutline || "inherit"};
            }

            .honeypot-field-w8i4k3l:hover {
                background-color: var(${HoneypotCssVariables.checkboxBgColorHover}, ${
                    hs.honeypotCheckBackgroundHover
                });
            }

            .honeypot-field-w8i4k3l:focus {
                background-color: var(${HoneypotCssVariables.checkboxBgColorFocus}, ${
                    hs.honeypotCheckBackgroundFocus
                });
            }

            .honeypot-field-w8i4k3l:checked {
                border-color: var(${HoneypotCssVariables.checkboxBorderColorChecked}, ${
                    hs.honeypotCheckBorderChecked
              });
                background-color: var(${HoneypotCssVariables.checkboxBgColorChecked}, ${
                    hs.honeypotCheckBackgroundChecked
              });
                background-image: var(${
                  HoneypotCssVariables.checkboxCheckmarkSvg
                }, url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" preserveAspectRatio="xMidYMid slice" viewBox="0 0 32 30" focusable="false" height="1rem" width="1rem"><path fill="none" d="M0 0h32v32H0z"></path><path fill="${hs.honeypotCheckMarker.replace(
                '#',
                '%23'
                )}" fill-rule="evenodd" d="M30 7l-1.41-1.41L11 23.17 3.41 15.6 2 17l9 9L30 7z"></path></svg>'));
            }

            .honeypot-field-w8i4k3l:required {
                border-color: var(${HoneypotCssVariables.checkboxBorderColorRequired}, ${
                    hs.honeypotCheckBorderRequired
                });
            }

            .honeypot-label-r7i2o5k {
                font-family: ${hs.honeypotFontFamily || "Arial, sans-serif"};
            }

            .honeypot-field-b2e4r52 {
                appearance: none;
                display: none;
                visibility: hidden;
            }

            .honeypot-label-s9o2p4k {
                display: none;
                visibility: hidden;
            }

            .honeypot-field-j3b1u7p {
                width: 0rem;
                height: 0rem;
                padding: 0rem;
                visibility: hidden;
                position: absolute;
            }

            .honeypot-label-v7g2l1j {
                width: 0rem;
                height: 0rem;
                padding: 0rem;
                visibility: hidden;
                position: absolute;
            }

            .honeypot-field-t0z1m4u {
                position: fixed;
                height: 0.625rem;
                width: 0.625rem;
                right: 0rem;
                top: 3.125rem;
                visibility: hidden;
                transform: translateX(150%);
            }

            .honeypot-label-7i2f9w {
                position: fixed;
                height: 0.625rem;
                width: 0.625rem;
                right: 0rem;
                top: 3.125rem;
                visibility: hidden;
                transform: translateX(150%);
            }

            /* Standard honeypot classes */

            .honeypot-div {
                margin-top: 4px;
                margin-bottom: 4px;
                color: ${hs.honeypotText};
            }

            .honeypot-div-security-check {
                font-family: inherit;
                margin-bottom: 16pt;
            }

            .honeypot-div-inputs {
                display: flex;
                align-items: center;
                margin-bottom: 8pt;
            }

            .honeypot-check-required {
                color: ${hs.honeypotCheckRequired};
                display: none;
                font-family: ${hs.honeypotFontFamily || "Arial, sans-serif"};
                margin-bottom: 8pt;
            }

            .honeypot-explanation {
                color: ${hs.honeypotExplanation};
                font-family: ${hs.honeypotFontFamily || "Arial, sans-serif"};
            }
        `;
        return styleTag;
    }
}
